import React, { useState } from 'react';
import { ArrowLeft, Upload, X, Percent, FileText, Send, AlertCircle } from 'lucide-react';
import { ItemDraft } from '../types';
import { DatePicker } from './DatePicker';
import { apiClient } from '../services/apiClient';

interface ProposalFormProps {
  onBack: () => void;
  onSubmit: (data: any) => void;
  selectedBrand?: string;
  initialData?: ItemDraft;
  aggregatorOptions?: { name: string; id: string; active?: boolean }[];
  initialProposalName?: string;
  currencyCode?: string;
}

export const ProposalForm: React.FC<ProposalFormProps> = ({ onBack, onSubmit, selectedBrand, initialData, aggregatorOptions = [], initialProposalName, currencyCode }) => {
  const currency = currencyCode || 'QAR';
  const [formData, setFormData] = useState({
    proposalName: initialProposalName || '',
    itemName: initialData?.name || '',
    currentPrice: initialData?.originalPrice || '',
    proposedPrice: initialData?.sellingPrice || '',
    discountType: 'Percentage' as 'Percentage' | 'Flat',
    discountValue: '',
    startDate: initialData?.startDate || '',
    endDate: initialData?.endDate || '',
    reason: initialData?.managerNote || '',
    description: initialData?.description || '',
  });

  const [selectedAggregators, setSelectedAggregators] = useState<string[]>(initialData?.selectedAggregators || []);
  const [image, setImage] = useState<string | null>(initialData?.image || null);
  const [imageName, setImageName] = useState<string>(initialData?.imageName || '');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  React.useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      proposalName: initialProposalName || '',
      itemName: initialData?.name || '',
      currentPrice: initialData?.originalPrice || '',
      proposedPrice: initialData?.sellingPrice || '',
      startDate: initialData?.startDate || '',
      endDate: initialData?.endDate || '',
      reason: initialData?.managerNote || '',
      description: initialData?.description || '',
    }));
    setSelectedAggregators(initialData?.selectedAggregators || []);
    setImage(initialData?.image || null);
    setImageName(initialData?.imageName || '');
  }, [initialData, initialProposalName]);

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsUploading(true);
    setUploadError(null);

    try {
      const res: any = await apiClient.uploads.uploadImage(file);
      const url = res?.url || (res?.key ? `/api/assets?key=${encodeURIComponent(res.key)}` : null);
      if (!url) throw new Error('No URL returned');
      setImage(url);
      setImageName(file.name);
    } catch (err) {
      setUploadError('Upload failed, using local preview only');
      const reader = new FileReader();
      reader.onloadend = () => {
        setImage(reader.result as string);
        setImageName(file.name);
      };
      reader.readAsDataURL(file);
    } finally {
      setIsUploading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    // Simulate API call
    setTimeout(() => {
      onSubmit({ ...formData, image, imageName, brand: selectedBrand, selectedAggregators });
      setIsSubmitting(false);
    }, 1000);
  };

  const calculateDiscount = () => {
    const current = parseFloat(formData.currentPrice);
    const proposed = parseFloat(formData.proposedPrice);
    if (!current || !proposed) return null;
    
    const diff = current - proposed;
    const percent = (diff / current) * 100;
    return { diff: diff.toFixed(2), percent: percent.toFixed(1) };
  };

  const impact = calculateDiscount();
  const parseDate = (value?: string | null) => {
    if (!value) return null;
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  };
  const safeStartDate = parseDate(formData.startDate);
  const safeEndDate = parseDate(formData.endDate);

  return (
    <div className="w-full max-w-3xl mx-auto pb-20">
      <div className="mb-8">
        <button 
          onClick={onBack}
          className="flex items-center gap-2 text-zinc-500 hover:text-teal-600 font-bold transition-all mb-6 hover:-translate-x-1"
        >
          <ArrowLeft className="w-5 h-5" /> Back to Dashboard
        </button>
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-extrabold text-zinc-900 tracking-tight">New Proposal</h1>
            <p className="text-zinc-500 mt-2">Propose a new discount or campaign for an item.</p>
          </div>
          {selectedBrand && (
             <div className="text-sm font-bold text-teal-700 bg-teal-50 px-4 py-2 rounded-full border border-teal-100 shadow-sm">
                Brand: {selectedBrand}
             </div>
          )}
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Section 1: Item Details */}
        <div className="bg-white rounded-3xl p-8 border border-zinc-200 shadow-sm">
          <h3 className="text-lg font-bold text-zinc-900 mb-6 flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-teal-50 flex items-center justify-center text-teal-600">1</div>
            Item Details
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="col-span-2">
              <label className="block text-xs font-bold text-zinc-500 uppercase tracking-wider mb-2">Proposal Name</label>
              <input 
                type="text" 
                required
                value={formData.proposalName}
                onChange={e => setFormData({...formData, proposalName: e.target.value})}
                className="w-full px-4 py-3 rounded-xl border border-zinc-200 focus:border-teal-500 focus:ring-4 focus:ring-teal-500/10 outline-none transition-all font-medium"
                placeholder="e.g. Summer Special Proposal"
              />
            </div>

            <div className="col-span-2">
              <label className="block text-xs font-bold text-zinc-500 uppercase tracking-wider mb-2">Item Name</label>
              <input 
                type="text" 
                required
                value={formData.itemName}
                onChange={e => setFormData({...formData, itemName: e.target.value})}
                className="w-full px-4 py-3 rounded-xl border border-zinc-200 focus:border-teal-500 focus:ring-4 focus:ring-teal-500/10 outline-none transition-all font-medium"
                placeholder="e.g. Signature Burger"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-zinc-500 uppercase tracking-wider mb-2">Current Price ({currency})</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs font-bold text-zinc-500">{currency}</span>
                <input 
                  type="number" 
                  required
                  min="0"
                  step="0.01"
                  value={formData.currentPrice}
                  onChange={e => setFormData({...formData, currentPrice: e.target.value})}
                  className="w-full pl-12 pr-4 py-3 rounded-xl border border-zinc-200 focus:border-teal-500 focus:ring-4 focus:ring-teal-500/10 outline-none transition-all font-medium"
                  placeholder="0.00"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-zinc-500 uppercase tracking-wider mb-2">Proposed Price ({currency})</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs font-bold text-zinc-500">{currency}</span>
                <input 
                  type="number" 
                  required
                  min="0"
                  step="0.01"
                  value={formData.proposedPrice}
                  onChange={e => setFormData({...formData, proposedPrice: e.target.value})}
                  className="w-full pl-12 pr-4 py-3 rounded-xl border border-zinc-200 focus:border-teal-500 focus:ring-4 focus:ring-teal-500/10 outline-none transition-all font-medium"
                  placeholder="0.00"
                />
              </div>
            </div>

            {impact && (
              <div className="col-span-2 bg-teal-50/50 rounded-xl p-4 border border-teal-100 flex items-center gap-4">
                <div className="p-2 bg-teal-100 rounded-lg text-teal-600">
                  <Percent className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-sm font-bold text-teal-900">Proposed Impact</p>
                  <p className="text-xs text-teal-700">
                    This is a <span className="font-bold">{impact.percent}% discount</span> ({currency} {impact.diff} reduction).
                  </p>
                </div>
              </div>
            )}

            <div className="col-span-2">
              <label className="block text-xs font-bold text-zinc-500 uppercase tracking-wider mb-2">Description</label>
              <textarea 
                rows={3}
                value={formData.description}
                onChange={e => setFormData({...formData, description: e.target.value})}
                className="w-full px-4 py-3 rounded-xl border border-zinc-200 focus:border-teal-500 focus:ring-4 focus:ring-teal-500/10 outline-none transition-all font-medium resize-none"
                placeholder="Describe the item..."
              />
            </div>
          </div>
        </div>

        {/* Section 2: Proposal Rationale */}
        <div className="bg-white rounded-3xl p-8 border border-zinc-200 shadow-sm">
          <h3 className="text-lg font-bold text-zinc-900 mb-6 flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-indigo-50 flex items-center justify-center text-indigo-600">2</div>
            Proposal Details
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <DatePicker
                label="Start Date"
                selected={safeStartDate}
                onChange={(date) => setFormData({...formData, startDate: date ? date.toISOString().split('T')[0] : ''})}
                placeholderText="Select start date"
                minDate={new Date()}
              />
            </div>
            <div>
              <DatePicker
                label="End Date"
                selected={safeEndDate}
                onChange={(date) => setFormData({...formData, endDate: date ? date.toISOString().split('T')[0] : ''})}
                placeholderText="Select end date"
                minDate={formData.startDate ? new Date(formData.startDate) : new Date()}
              />
            </div>

            <div className="col-span-2">
              <label className="block text-xs font-bold text-zinc-500 uppercase tracking-wider mb-2">Reason for Proposal</label>
              <textarea 
                required
                rows={4}
                value={formData.reason}
                onChange={e => setFormData({...formData, reason: e.target.value})}
                className="w-full px-4 py-3 rounded-xl border border-zinc-200 focus:border-teal-500 focus:ring-4 focus:ring-teal-500/10 outline-none transition-all font-medium resize-none"
                placeholder="Why should we approve this discount? e.g. Stock clearance, Competitor matching..."
              />
            </div>

            <div className="col-span-2">
              <label className="block text-xs font-bold text-zinc-500 uppercase tracking-wider mb-2">Participating Platforms</label>
              {aggregatorOptions.length === 0 ? (
                <p className="text-xs text-zinc-500 bg-zinc-50 border border-zinc-200 rounded-xl px-3 py-2">No platforms configured. Add them in System Settings.</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {aggregatorOptions.filter((a) => a.active !== false).map((agg) => {
                    const selected = selectedAggregators.includes(agg.name);
                    return (
                      <button
                        type="button"
                        key={agg.id}
                        onClick={() => {
                          setSelectedAggregators((prev) =>
                            prev.includes(agg.name) ? prev.filter((p) => p !== agg.name) : [...prev, agg.name]
                          );
                        }}
                        className={`px-4 py-2 rounded-xl text-xs font-bold border transition-all ${selected ? 'bg-teal-600 text-white border-teal-600 shadow-md shadow-teal-200' : 'bg-white text-zinc-600 border-zinc-200 hover:border-teal-200 hover:text-teal-700'}`}
                      >
                        {agg.name}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Section 3: Image */}
        <div className="bg-white rounded-3xl p-8 border border-zinc-200 shadow-sm">
          <h3 className="text-lg font-bold text-zinc-900 mb-6 flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-rose-50 flex items-center justify-center text-rose-600">3</div>
            Item Image
          </h3>

          <div className="flex items-center justify-center w-full">
            {image ? (
              <div className="relative w-full h-64 rounded-2xl overflow-hidden group">
                <img src={image} alt="Preview" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <button 
                    type="button"
                    onClick={() => setImage(null)}
                    className="bg-white text-red-500 px-4 py-2 rounded-lg font-bold hover:bg-red-50 transition-colors"
                  >
                    Remove Image
                  </button>
                </div>
              </div>
            ) : (
              <label className="flex flex-col items-center justify-center w-full h-64 border-2 border-zinc-300 border-dashed rounded-2xl cursor-pointer hover:bg-zinc-50 hover:border-teal-400 transition-all group">
                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                  <div className="w-16 h-16 bg-zinc-100 rounded-full flex items-center justify-center mb-4 group-hover:bg-teal-50 group-hover:text-teal-600 transition-colors text-zinc-400">
                    {isUploading ? <Upload className="w-8 h-8 animate-pulse" /> : <Upload className="w-8 h-8" />}
                  </div>
                  <p className="mb-2 text-sm text-zinc-500 font-medium">
                    <span className="font-bold text-zinc-900">Click to upload</span> or drag and drop
                  </p>
                  <p className="text-xs text-zinc-400">SVG, PNG, JPG or GIF (MAX. 800x400px)</p>
                  {uploadError && <p className="text-[11px] text-amber-600 font-bold mt-1">{uploadError}</p>}
                </div>
                <input type="file" className="hidden" accept="image/*" onChange={handleImageUpload} />
              </label>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-4 pt-4">
          <button
            type="button"
            onClick={onBack}
            className="px-8 py-4 rounded-xl font-bold text-zinc-500 hover:bg-zinc-100 transition-all"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="flex items-center gap-2 px-10 py-4 bg-teal-600 text-white rounded-xl font-bold shadow-lg shadow-teal-200 hover:bg-teal-700 hover:-translate-y-1 transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Submitting...' : (
              <>
                Submit Proposal <Send className="w-5 h-5" />
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};
